import { useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import type { GlobalFile } from '../../../api/types';
import { formatUploadDate } from '../../../utils/formatDate';
import { formatFileSize } from '../utils/tagStyles';
import documentsApi from '../../../api/documentsApi';
import googleSheetsIntegrationApi from '../../../api/googleSheetsIntegrationApi';
import { formatGoogleSheetsWarningsForDisplay } from '../utils/googleSheetsWarnings';
import { buildGoogleSheetEmbedUrl } from '../utils/googleSheetsEmbed';

interface DocumentMetadataDrawerProps {
    file: GlobalFile | null;
    onClose: () => void;
    /** When set, show support actions (re-classify from server). */
    businessId?: string;
    allowReclassify?: boolean;
    /** Called after successful reclassification (refresh lists / tree). */
    onWorkspaceReclassified?: () => void;
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
    const display = value === null || value === undefined || value === '' ? '—' : String(value);
    return (
        <div className="py-2 border-b border-gray-100 last:border-0">
            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</div>
            <div className="text-sm text-gray-900 mt-0.5 break-words">{display}</div>
        </div>
    );
}

export default function DocumentMetadataDrawer({
    file,
    onClose,
    businessId,
    allowReclassify = false,
    onWorkspaceReclassified,
}: DocumentMetadataDrawerProps) {
    const [reclassifying, setReclassifying] = useState(false);
    const [reclassifyError, setReclassifyError] = useState<string | null>(null);
    const [forceLlm] = useState(false);
    const [showAdvancedMetadata, setShowAdvancedMetadata] = useState(false);
    const [gsResyncing, setGsResyncing] = useState(false);
    const [showSheetEmbed, setShowSheetEmbed] = useState(true);

    if (!file) return null;

    const isGoogleSheetFile = file.source_channel === 'google_sheets' && Boolean(file.external_document);
    const sheetEmbedUrl = isGoogleSheetFile
        ? buildGoogleSheetEmbedUrl(file.external_document?.spreadsheet_url)
        : null;
    const sheetOpenUrl = file.external_document?.spreadsheet_url?.trim();

    const handleReclassify = async () => {
        setReclassifyError(null);
        setReclassifying(true);
        try {
            await documentsApi.reclassifyWorkspaceFile(file.file_id, {
                businessId,
                forceLlm,
            });
            onWorkspaceReclassified?.();
            onClose();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Reclassification failed';
            setReclassifyError(msg);
        } finally {
            setReclassifying(false);
        }
    };

    return (
        <>
            <button
                type="button"
                aria-label="Close details"
                className="fixed inset-0 bg-black/30 z-[2090] md:bg-black/20"
                onClick={onClose}
            />
            <div
                className={`fixed top-0 right-0 bottom-0 z-[2100] w-full bg-white shadow-2xl flex flex-col border-l border-gray-200 ${
                    isGoogleSheetFile ? 'max-w-2xl' : 'max-w-md'
                }`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="doc-metadata-title"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <h2 id="doc-metadata-title" className="text-base font-semibold text-gray-900 truncate pr-2">
                        {isGoogleSheetFile ? 'Google Sheet' : 'Document details'}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="overflow-y-auto flex-1 px-4 py-3">
                    {isGoogleSheetFile && (sheetEmbedUrl || sheetOpenUrl) ? (
                        <div className="mb-4 pb-4 border-b border-gray-200">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                                Preview in Finz
                            </div>
                            <p className="text-xs text-gray-500 mb-2">
                                Uses your Google account in this browser. If the preview is blank, use “Open in new tab”
                                below.
                            </p>
                            {showSheetEmbed && sheetEmbedUrl ? (
                                <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50 min-h-[280px] h-[min(55vh,520px)]">
                                    <iframe
                                        title="Google Sheet preview"
                                        src={sheetEmbedUrl}
                                        className="w-full h-full min-h-[280px] border-0"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                    />
                                </div>
                            ) : null}
                            <div className="flex flex-wrap gap-3 mt-2 items-center">
                                {sheetEmbedUrl ? (
                                    <button
                                        type="button"
                                        className="text-xs font-medium text-indigo-600 hover:underline"
                                        onClick={() => setShowSheetEmbed((v) => !v)}
                                    >
                                        {showSheetEmbed ? 'Hide preview' : 'Show preview'}
                                    </button>
                                ) : null}
                                {sheetOpenUrl ? (
                                    <a
                                        href={sheetOpenUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-medium text-indigo-600 hover:underline"
                                    >
                                        Open in Google Sheets (new tab)
                                    </a>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    <Row label="Filename" value={file.original_filename} />
                    <Row label="Upload date" value={formatUploadDate(file.upload_time)} />
                    <Row label="Size" value={file.file_size != null ? formatFileSize(file.file_size) : undefined} />
                    <Row label="Document type" value={file.type} />
                    <Row label="Current folder" value={file.workspace_folder_path ?? undefined} />
                    {file.source_channel === 'email' && (
                        <>
                            <Row label="Source" value="Email" />
                            <Row label="From (email)" value={file.source_sender_email ?? undefined} />
                            <Row label="Email subject" value={file.source_subject ?? undefined} />
                            <Row label="Ingestion email id" value={file.email_id ?? undefined} />
                        </>
                    )}
                    {file.source_channel === 'google_sheets' && file.external_document && (
                        <>
                            <Row label="Source" value="Google Sheets" />
                            <Row label="Sheet type" value={file.external_document.sheet_type ?? undefined} />
                            <Row
                                label="Confidence"
                                value={
                                    file.external_document.classification_confidence != null
                                        ? `${Math.round(Number(file.external_document.classification_confidence) * 100)}%`
                                        : undefined
                                }
                            />
                            <Row label="Review" value={file.external_document.review_status ?? undefined} />
                            <Row label="Folder" value={file.external_document.parent_folder_name ?? undefined} />
                            <Row
                                label="Tabs"
                                value={
                                    file.external_document.tabs?.length
                                        ? file.external_document.tabs.join(', ')
                                        : undefined
                                }
                            />
                            <Row label="Active version" value={file.external_document.active_version_id ?? undefined} />
                            {file.external_document.warnings && file.external_document.warnings.length > 0 && (
                                <Row
                                    label="Warnings"
                                    value={formatGoogleSheetsWarningsForDisplay(file.external_document.warnings)}
                                />
                            )}
                            {file.google_sheet_source_document_id && (
                                <div className="pt-2">
                                    <button
                                        type="button"
                                        disabled={gsResyncing}
                                        onClick={() => {
                                            void (async () => {
                                                setGsResyncing(true);
                                                try {
                                                    await googleSheetsIntegrationApi.resync(
                                                        file.google_sheet_source_document_id!
                                                    );
                                                    onWorkspaceReclassified?.();
                                                } catch (e: unknown) {
                                                    const msg =
                                                        e && typeof e === 'object' && 'response' in e
                                                            ? String(
                                                                  (e as { response?: { data?: { detail?: string } } })
                                                                      .response?.data?.detail || ''
                                                              )
                                                            : e instanceof Error
                                                              ? e.message
                                                              : 'Re-sync failed';
                                                    window.alert(msg);
                                                } finally {
                                                    setGsResyncing(false);
                                                }
                                            })();
                                        }}
                                        className="inline-flex w-full justify-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                                    >
                                        {gsResyncing ? 'Re-syncing…' : 'Re-sync from Google'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    <div className="pt-3">
                        <button
                            type="button"
                            onClick={() => setShowAdvancedMetadata((prev) => !prev)}
                            className="text-sm font-medium text-gray-600 hover:text-gray-900"
                        >
                            {showAdvancedMetadata ? 'Hide advanced details' : 'Show advanced details'}
                        </button>
                    </div>
                    {showAdvancedMetadata && (
                        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <Row label="Workspace category" value={file.workspace_category ?? undefined} />
                            <Row label="Entity / institution" value={file.workspace_entity ?? undefined} />
                            <Row label="Classification" value={file.workspace_classification_status ?? undefined} />
                            <Row label="Source" value={file.classification_source ?? undefined} />
                            <Row
                                label="Confidence"
                                value={
                                    file.classification_confidence != null
                                        ? `${Math.round(Number(file.classification_confidence) * 100)}%`
                                        : undefined
                                }
                            />
                            <Row label="Reporting period start" value={file.reporting_period_start ?? undefined} />
                            <Row label="Reporting period end" value={file.reporting_period_end ?? undefined} />
                            <Row label="RAG indexed at" value={file.rag_indexed_at ?? undefined} />
                        </div>
                    )}
                </div>
                {allowReclassify && businessId && (
                    <div className="border-t border-gray-200 px-4 py-3 space-y-3 bg-gray-50/80">
                        {/* <p className="text-xs text-gray-600">
                            Re-download from storage and re-run folder classification. This updates sidebar metadata only;
                            it does not re-build search (Pinecone) vectors for already-indexed files.
                        </p>
                        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={forceLlm}
                                onChange={(ev) => setForceLlm(ev.target.checked)}
                                className="rounded border-gray-300"
                            />
                            Force LLM step (slower; uses more tokens)
                        </label> */}
                        {reclassifyError && (
                            <p className="text-xs text-red-600">{reclassifyError}</p>
                        )}
                        <button
                            type="button"
                            onClick={() => void handleReclassify()}
                            disabled={reclassifying}
                            className="inline-flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <RefreshCw className={`w-4 h-4 ${reclassifying ? 'animate-spin' : ''}`} />
                            {reclassifying ? 'Re-classifying…' : 'Re-classify workspace'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
